import { RefObject, useLayoutEffect, useSyncExternalStore } from 'react';

const helpdeskBottomOffsetsByElement = new Map<HTMLElement, number>();
const helpdeskBottomOffsetListeners = new Set<() => void>();

const getElementHeight = (element: HTMLElement) => {
  const { height } = element.getBoundingClientRect();

  return Math.round(height || element.offsetHeight);
};

const getResolvedHelpdeskBottomOffsetPx = () => {
  return Array.from(helpdeskBottomOffsetsByElement.values()).reduce(
    (resolvedBottomOffsetPx, nextBottomOffsetPx) =>
      Math.max(resolvedBottomOffsetPx, nextBottomOffsetPx),
    0
  );
};

const notifyHelpdeskBottomOffsetListeners = () => {
  helpdeskBottomOffsetListeners.forEach((listener) => listener());
};

const subscribeToHelpdeskBottomOffset = (listener: () => void) => {
  helpdeskBottomOffsetListeners.add(listener);

  return () => {
    helpdeskBottomOffsetListeners.delete(listener);
  };
};

const setElementBottomOffset = (
  element: HTMLElement,
  nextBottomOffsetPx: number
) => {
  if (helpdeskBottomOffsetsByElement.get(element) === nextBottomOffsetPx) {
    return;
  }

  helpdeskBottomOffsetsByElement.set(element, nextBottomOffsetPx);
  notifyHelpdeskBottomOffsetListeners();
};

const clearElementBottomOffset = (element: HTMLElement) => {
  if (!helpdeskBottomOffsetsByElement.delete(element)) {
    return;
  }

  notifyHelpdeskBottomOffsetListeners();
};

export const useResolvedHelpdeskBottomOffset = () => {
  return useSyncExternalStore(
    subscribeToHelpdeskBottomOffset,
    getResolvedHelpdeskBottomOffsetPx,
    () => 0
  );
};

export const usePlaceHelpdeskAboveElement = (
  elementRef: RefObject<HTMLElement | null>,
  spacingPx = 0
) => {
  useLayoutEffect(() => {
    const element = elementRef.current;

    if (!element) {
      return undefined;
    }

    const placeHelpdeskAboveElement = () => {
      setElementBottomOffset(element, getElementHeight(element) + spacingPx);
    };

    placeHelpdeskAboveElement();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(placeHelpdeskAboveElement)
        : null;

    resizeObserver?.observe(element);
    if (!resizeObserver) {
      window.addEventListener('resize', placeHelpdeskAboveElement);
    }

    return () => {
      resizeObserver?.disconnect();
      if (!resizeObserver) {
        window.removeEventListener('resize', placeHelpdeskAboveElement);
      }
      clearElementBottomOffset(element);
    };
  }, [elementRef, spacingPx]);
};
